import React from 'react'

type ToastVariant = 'info' | 'success' | 'error'

interface Toast {
  id: number
  message: string
  variant: ToastVariant
}

interface ToastContextValue {
  showToast: (message: string, variant?: ToastVariant) => void
}

const ToastContext = React.createContext<ToastContextValue | null>(null)

/** Access the toast trigger. Must be used under a {@link ToastProvider}. */
export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>')
  }
  return ctx
}

const TOAST_TTL_MS = 4000

/**
 * App-light toast provider (no external deps).
 *
 * Mounted in the login layout, so a toast fired right before a client-side
 * navigation (e.g. from the email form to the OTP step) stays on screen across
 * the route change. Toasts auto-dismiss after {@link TOAST_TTL_MS}.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Array<Toast>>([])
  const nextId = React.useRef(0)

  const dismiss = React.useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const showToast = React.useCallback(
    (message: string, variant: ToastVariant = 'info') => {
      const id = (nextId.current += 1)
      setToasts((prev) => [...prev, { id, message, variant }])
      setTimeout(() => dismiss(id), TOAST_TTL_MS)
    },
    [dismiss],
  )

  const value = React.useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Array<Toast>
  onDismiss: (id: number) => void
}) {
  if (toasts.length === 0) return null

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => onDismiss(toast.id)} />
      ))}
    </div>
  )
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const variantClasses: Record<ToastVariant, string> = {
    success: 'border-green-200 bg-green-50 text-green-800',
    error: 'border-red-200 bg-red-50 text-red-700',
    info: 'border-brand-border-light bg-white text-brand-dark-text',
  }

  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-lg border p-3 text-sm leading-relaxed shadow-lg ${variantClasses[toast.variant]}`}
    >
      <span className="flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="shrink-0 text-base leading-none opacity-60 transition-opacity hover:opacity-100"
      >
        ×
      </button>
    </div>
  )
}
